import React, { useCallback, useMemo, useState } from 'react'
import AddMappingPane from './Components/AddMappingPane'
import AttributeMappingList from './Components/AttributeMappingList'
import DirectionSwitch from './Components/DirectionSwitch'
import StatusBanner from '../Shared/Components/StatusBanner'
import useProductAttributeMapping, { isRowComplete } from './useProductAttributeMapping'
import { SOURCE_TO_TARGET, TARGET_TO_SOURCE } from './productAttributeApi'
import { rightSide } from './attributeDirection'
import Shared from '../Shared/mappingUi.module.css'
import Style from './productAttribute.module.css'

const ProductAttributeMapping = ({ connectorId, instancesId, sourceShortName, targetShortName }) => {
    const [direction, setDirection] = useState(SOURCE_TO_TARGET)
    const [isPaneOpen, setIsPaneOpen] = useState(false)

    const {
        erpFields,
        ecommerceFields,
        rows,
        isLoading,
        loadError,
        isSaving,
        isDirty,
        saveStatus,
        addMappings,
        updateRow,
        removeRow,
        submitMappings,
        dismissStatus
    } = useProductAttributeMapping(connectorId, instancesId, direction)

    const erpTitle = sourceShortName || 'ERP'
    const ecommerceTitle = targetShortName || 'eCommerce'
    const leftTitle = direction.leftIsErp ? erpTitle : ecommerceTitle
    const rightTitle = direction.leftIsErp ? ecommerceTitle : erpTitle

    const flipDirection = useCallback(() => {
        setDirection((current) => (current === SOURCE_TO_TARGET ? TARGET_TO_SOURCE : SOURCE_TO_TARGET))
    }, [])

    // Each right-hand field can only be the destination of one mapping.
    const mappedRightNames = useMemo(
        () => rows.map((row) => rightSide(row, direction).name),
        [rows, direction]
    )

    const completeRowCount = useMemo(
        () => rows.filter((row) => isRowComplete(row, direction)).length,
        [rows, direction]
    )
    const hasIncompleteRow = completeRowCount !== rows.length

    const isBusy = isLoading || isSaving
    const isBlocked = isBusy || Boolean(loadError)

    return (
        <>
            <div className={`card-header p-10 ${Shared.header}`}>
                <div className={Shared.headerTitle}>
                    <span>Product Attribute Mapping</span>
                    {!isLoading && !loadError ? (
                        <span className={Shared.countChip}>{completeRowCount} mapped</span>
                    ) : null}
                </div>

                <DirectionSwitch
                    leftTitle={leftTitle}
                    rightTitle={rightTitle}
                    isDisabled={isBusy}
                    onFlip={flipDirection}
                />

                <div className={Style.headerActions}>
                    <button
                        type="button"
                        className={Style.secondaryButton}
                        disabled={isBlocked}
                        onClick={() => setIsPaneOpen(true)}
                    >
                        <i className="uil-plus" aria-hidden="true" /> Add New Mappings
                    </button>
                    <button
                        type="button"
                        className={Shared.submitButton}
                        disabled={isBlocked || !isDirty}
                        onClick={submitMappings}
                    >
                        {isSaving ? <span className={Shared.buttonSpinner} aria-hidden="true" /> : null}
                        {isSaving ? 'Saving...' : 'Submit Mappings'}
                    </button>
                </div>
            </div>

            <div className={`card-body p-10 ${Shared.body}`}>
                <StatusBanner status={saveStatus} onDismiss={dismissStatus} />

                {isDirty && !isSaving ? (
                    <p className={Shared.hint}>
                        <i className="uil-info-circle" aria-hidden="true" />
                        {' '}You have unsaved changes. Submit the mappings to keep them.
                    </p>
                ) : null}

                {hasIncompleteRow ? (
                    <p className={Shared.hint}>
                        <i className="uil-info-circle" aria-hidden="true" />
                        {' '}Rows with an empty field are skipped when you submit.
                    </p>
                ) : null}

                <AttributeMappingList
                    rows={rows}
                    direction={direction}
                    leftTitle={leftTitle}
                    rightTitle={rightTitle}
                    erpFields={erpFields}
                    ecommerceFields={ecommerceFields}
                    isLoading={isLoading}
                    loadError={loadError}
                    isDisabled={isSaving}
                    onChange={updateRow}
                    onRemove={removeRow}
                />
            </div>

            <AddMappingPane
                isOpen={isPaneOpen}
                direction={direction}
                leftTitle={leftTitle}
                rightTitle={rightTitle}
                erpFields={erpFields}
                ecommerceFields={ecommerceFields}
                mappedRightNames={mappedRightNames}
                onAdd={addMappings}
                onClose={() => setIsPaneOpen(false)}
            />
        </>
    )
}

export default ProductAttributeMapping
